import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Modal } from 'components/UI/Modal'
import { useSelectLocationsForDealTableColumn } from 'components/Tables/SelectLocationsForDealTable/useSelectLocationsForDealTableColumn'
import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import useApiService from 'services/api'

const PAGE_SIZE = 100

interface ViewDealLocationsModalProps {
  show: boolean
  onHide: () => void
  contactCompanyId: number
  companyName: string
  totalDoors: number
}

/**
 * Read-only modal showing all locations for a deal's contact company.
 * Reuses the same doors-for-selection API and columns as EditDealLocationsModal
 * but without selection or editing capabilities.
 */
export function ViewDealLocationsModal({
  show,
  onHide,
  contactCompanyId,
  companyName,
  totalDoors,
}: ViewDealLocationsModalProps) {
  if (!show) return null

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="[&]:px-6 [&]:py-5 [&]:border-b-0">
        <Modal.Title>
          {companyName} Locations
          <span className="text-fg-secondary font-normal ml-2">
            ({totalDoors.toLocaleString()})
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="[&]:px-6 [&]:pb-6 [&]:pt-0">
        <ColumnsStoreProvider tableKey={`VIEW_LOCATIONS_${contactCompanyId}`}>
          <DataTableProvider tableKey={`VIEW_LOCATIONS_${contactCompanyId}`}>
            <ViewLocationsTable contactCompanyId={contactCompanyId} />
          </DataTableProvider>
        </ColumnsStoreProvider>
      </Modal.Body>
    </Modal>
  )
}

function ViewLocationsTable({
  contactCompanyId,
}: {
  contactCompanyId: number
}) {
  const api = useApiService()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')

  const { data, isFetching } = useQuery({
    queryKey: ['view-deal-locations', contactCompanyId, page, search],
    queryFn: ({ signal }) =>
      api.getDoorsForSelection(
        contactCompanyId,
        {
          page,
          limit: PAGE_SIZE,
          ...(search ? { search } : {}),
        },
        {},
        signal
      ),
  })

  const allColumns = useSelectLocationsForDealTableColumn()
  // Remove the 'name' column — redundant since all rows are the same company
  const columns = useMemo(
    () => allColumns.filter((col) => (col as any).accessorKey !== 'name'),
    [allColumns]
  )

  return (
    <DataTableContainer className="h-[500px] flex">
      <DataTable
        tableHeader={
          <div className="mb-2">
            <input
              type="text"
              placeholder="Search locations..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full px-3 py-2 border border-line rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>
        }
        tableKey={`VIEW_LOCATIONS_${contactCompanyId}`}
        loading={isFetching}
        data={data?.results ?? []}
        columns={columns}
        sortableFields={[
          'name',
          'address',
          'city',
          'state',
          'zipcode',
          'dma',
          'domain',
        ]}
        virtualizeRows={false}
        isPaginationEnabled
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage,
          page,
          isPaginationLoading: isFetching,
        }}
      />
    </DataTableContainer>
  )
}
